import { useCallback } from 'react';
import { useUpdatePlayerInfo } from 'server';
import { useFetchUserDetails } from 'js/hooks/user';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useGetErrorMessage } from 'js/hooks/server/hooks';
import { IDynamicChangeDetailsData } from 'js/components/main/DynamicChangeDetails/types';
import { useRequestTwoFactorAuthentication } from 'js/components/popups/TwoFactorAuthenticationPopup';
import { useRequestMultiFactorAuthentication } from 'js/components/popups/MultiFactorAuthenticationPopup';
import { ICombinedSubmitOptions } from 'js/components/common/ChangeForm/hooks/useOnCombinedSubmit/types';

import { NO_NEED_VERIFICATION_STATUSES } from '../constants';

export const useChangeDetails = ({ onSuccess }: { onSuccess: () => void }) => {
    const isMounted = useIsMounted();
    const updatePlayerInfo = useUpdatePlayerInfo();
    const getErrorMessage = useGetErrorMessage();
    const fetchUserDetails = useFetchUserDetails();
    const requestTwoFactorAuthentication = useRequestTwoFactorAuthentication();
    const requestMultiFactorAuthentication = useRequestMultiFactorAuthentication();

    const submit = useCallback(({
        addressDetails,
        dataMap,
        fields,
        getUserValues,
        setValuesAfterSubmit
    }: IDynamicChangeDetailsData): Promise<string | void> => {
        return new Promise((resolve, reject) => {
            const process = async (): Promise<void> => {
                try {
                    await updatePlayerInfo({ dataMap, addressDetails });
                    const userDetails = await fetchUserDetails();

                    if (NO_NEED_VERIFICATION_STATUSES.includes(userDetails.ageVerificationStatus) && isMounted()) {
                        onSuccess();
                    }

                    const userValues = getUserValues({ fields, userDetails });

                    setValuesAfterSubmit((prevValues) => ({ ...prevValues, ...userValues }));
                    resolve();
                } catch (error: any) {
                    const { data: { errorCode = 0 } = {} } = error?.response || error || {};

                    if (errorCode === 303) {
                        try {
                            await requestTwoFactorAuthentication({
                                initiator: 'change-details',
                                onSuccess: () => process(),
                                onCancel: resolve
                            });
                        } catch {
                            // player has canceled the process
                        }
                    } else if (errorCode === 304) {
                        try {
                            await requestMultiFactorAuthentication({
                                initiator: 'change-details',
                                onSuccess: () => process(),
                                onCancel: resolve
                            });
                        } catch {
                            // player has canceled the process
                        }
                    } else {
                        reject(error);
                    }
                }
            };

            return process();
        });
    }, [
        onSuccess,
        isMounted,
        updatePlayerInfo,
        fetchUserDetails,
        requestMultiFactorAuthentication,
        requestTwoFactorAuthentication
    ]);

    return useCallback(async ({
        dataMap, addressDetails, setValuesAfterSubmit, fields, getUserValues
    }: ICombinedSubmitOptions): Promise<string | void> => {
        try {
            await submit({ dataMap, addressDetails, setValuesAfterSubmit, fields, getUserValues });
        } catch (error) {
            return getErrorMessage(error);
        }
    }, [submit, getErrorMessage]);
};
